import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plant,
  PaperPlaneTilt,
  CheckCircle,
  Clock,
  Pause,
  StopCircle,
  Calendar,
  WhatsappLogo,
  EnvelopeSimple,
  ChatTeardropText,
  Lightning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const getJourneyLabel = (value) => {
  const labels = {
    pronto_atendimento: 'Pronto para Atendimento',
    em_avaliacao: 'Em Avaliação',
    nutricao_futura: 'Nutrição Futura',
    quente: 'Pronto para Atendimento',
    morno: 'Em Avaliação',
    frio: 'Nutrição Futura',
  };
  return labels[value] || value;
};

const getJourneyBadgeClass = (value) => {
  if (value === 'pronto_atendimento' || value === 'quente') return 'bg-emerald-100 text-emerald-700';
  if (value === 'em_avaliacao' || value === 'morno') return 'bg-orange-100 text-orange-700';
  return 'bg-slate-100 text-slate-700';
};


const API = `/api`;

const NurtureSequence = ({ leadId, leadName, isAdmin }) => {
  const [sequence, setSequence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState('whatsapp');
  const [demoMode, setDemoMode] = useState(true);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (leadId) fetchSequence();
  }, [leadId]);

  const fetchSequence = async () => {
    try {
      const { data } = await axios.get(`${API}/nurture/${leadId}`, { withCredentials: true });
      setSequence(data.sequence);
    } catch (error) {
      console.error('Error fetching sequence:', error);
    } finally {
      setLoading(false);
    }
  };

  const startSequence = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post(
        `${API}/nurture/start`,
        { lead_id: leadId, channel, demo_mode: demoMode },
        { withCredentials: true }
      );
      setSequence(data);
      toast.success('Sequência de nutrição iniciada! IA gerou 5 mensagens.');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao iniciar sequência';
      toast.error(typeof detail === 'string' ? detail : 'Erro ao iniciar sequência');
    } finally {
      setStarting(false);
    }
  };

  const stopSequence = async () => {
    if (!window.confirm('Deseja realmente parar a sequência de nutrição?')) return;
    setStopping(true);
    try {
      await axios.post(`${API}/nurture/${leadId}/stop`, {}, { withCredentials: true });
      toast.success('Sequência interrompida');
      fetchSequence();
    } catch (error) {
      toast.error('Erro ao parar sequência');
    } finally {
      setStopping(false);
    }
  };

  const processNow = async () => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/nurture/process`, {}, { withCredentials: true });
      if (data.processed > 0) {
        toast.success(`${data.processed} touchpoint(s) enviado(s)!`);
      } else {
        toast.info('Nenhum touchpoint vencido para processar');
      }
      fetchSequence();
    } catch (error) {
      toast.error('Erro ao processar');
    } finally {
      setProcessing(false);
    }
  };

  const getChannelIcon = (ch) => {
    if (ch === 'whatsapp') return <WhatsappLogo size={14} weight="fill" className="text-emerald-600" />;
    if (ch === 'email') return <EnvelopeSimple size={14} weight="duotone" className="text-blue-600" />;
    return <ChatTeardropText size={14} weight="duotone" className="text-purple-600" />;
  };

  const getTouchpointStatusBadge = (status) => {
    const styles = {
      pending: 'bg-slate-100 text-slate-700',
      sent: 'bg-emerald-100 text-emerald-700',
      skipped: 'bg-orange-100 text-orange-700',
    };
    const labels = {
      pending: 'Agendado',
      sent: 'Enviado',
      skipped: 'Pulado',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[status] || 'bg-slate-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getSequenceStatusBadge = () => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      paused: 'bg-orange-100 text-orange-700 border-orange-300',
      completed: 'bg-blue-100 text-blue-700 border-blue-300',
      stopped: 'bg-slate-100 text-slate-700 border-slate-300',
    };
    const labels = {
      active: 'Ativa',
      paused: 'Pausada (auto)',
      completed: 'Concluída',
      stopped: 'Interrompida',
    };
    return (
      <span className={`text-xs px-3 py-1 rounded-full font-medium border ${styles[sequence?.status] || ''}`}>
        {labels[sequence?.status] || sequence?.status}
      </span>
    );
  };

  const getStopReasonLabel = (reason) => {
    const labels = {
      lead_responded: 'Lead respondeu - sequência pausada automaticamente',
      classification_hot: 'Lead ficou Pronto para Atendimento - sequência pausada automaticamente',
      manual: 'Interrompida manualmente pelo time',
      all_sent: 'Todos os 5 touchpoints foram enviados',
    };
    return labels[reason] || reason;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8">
      <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }

  // No sequence yet - show start form
  if (!sequence) {
    return (
      <div className="space-y-5" data-testid="nurture-start-form">
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Plant size={20} weight="fill" className="text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">Sequência Automática de Nutrição</h4>
              <p className="text-xs text-slate-600">5 touchpoints inteligentes para {leadName}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 mb-4 border border-slate-200">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2 font-semibold">Como funciona</p>
            <div className="grid grid-cols-5 gap-2 mt-3">
              {['D+1', 'D+3', 'D+7', 'D+15', 'D+30'].map((day, i) => (
                <div key={day} className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-emerald-100 text-emerald-700' :
                    i === 1 ? 'bg-blue-100 text-blue-700' :
                    i === 2 ? 'bg-purple-100 text-purple-700' :
                    i === 3 ? 'bg-orange-100 text-orange-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {day}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">touchpoint</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-600 mt-4 leading-relaxed">
              <strong>Auto-pause inteligente:</strong> A sequência para automaticamente se o lead responder ou virar QUENTE.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 block">Canal de envio</label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                data-testid="nurture-channel-select"
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="email">E-mail</option>
                <option value="sms">SMS</option>
              </select>
            </div>

            <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={demoMode}
                onChange={(e) => setDemoMode(e.target.checked)}
                className="mt-0.5"
                data-testid="nurture-demo-mode-checkbox"
              />
              <div>
                <p className="text-xs font-semibold text-amber-900">Modo demonstração</p>
                <p className="text-xs text-amber-700 mt-0.5">Intervalos em minutos (1min, 3min, 7min, 15min, 30min) ao invés de dias - útil para apresentações.</p>
              </div>
            </label>
          </div>
        </div>

        <button
          onClick={startSequence}
          disabled={starting}
          className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="start-nurture-button"
        >
          {starting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              IA gerando 5 mensagens personalizadas... (~30s)
            </>
          ) : (
            <>
              <Plant size={18} weight="fill" />
              Iniciar Sequência Automática
            </>
          )}
        </button>
      </div>
    );
  }

  // Has sequence - show timeline
  const sentCount = sequence.touchpoints.filter((tp) => tp.status === 'sent').length;
  const totalCount = sequence.touchpoints.length;

  return (
    <div className="space-y-4" data-testid="nurture-sequence-view">
      {/* Status Header */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Plant size={20} weight="fill" className="text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">Sequência de Nutrição</p>
              <p className="text-xs text-slate-500">
                {sentCount}/{totalCount} touchpoints enviados · Canal: {sequence.channel}
                {sequence.demo_mode && ' · Modo demo'}
              </p>
            </div>
          </div>
          {getSequenceStatusBadge()}
        </div>

        {sequence.stop_reason && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            <Lightning size={14} weight="fill" className="inline mr-1" />
            {getStopReasonLabel(sequence.stop_reason)}
          </div>
        )}

        {sequence.status === 'active' && (
          <div className="flex gap-2 mt-3">
            {isAdmin && (
              <button
                onClick={processNow}
                disabled={processing}
                className="flex-1 px-3 py-2 text-sm border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="process-nurture-button"
              >
                {processing ? (
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <PaperPlaneTilt size={14} weight="duotone" />
                )}
                Processar pendentes
              </button>
            )}
            <button
              onClick={stopSequence}
              disabled={stopping}
              className="flex-1 px-3 py-2 text-sm border border-red-300 text-red-700 hover:bg-red-50 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="stop-nurture-button"
            >
              <StopCircle size={14} weight="duotone" />
              Parar Sequência
            </button>
          </div>
        )}
      </div>

      {/* Touchpoints Timeline */}
      <div className="space-y-3">
        {sequence.touchpoints.map((tp, index) => (
          <div
            key={index}
            className={`border rounded-lg p-4 ${
              tp.status === 'sent'
                ? 'bg-emerald-50/50 border-emerald-200'
                : 'bg-white border-slate-200'
            }`}
            data-testid={`touchpoint-${tp.day}`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm ${
                  tp.status === 'sent'
                    ? 'bg-emerald-500 text-white'
                    : index === 0 ? 'bg-emerald-100 text-emerald-700' :
                      index === 1 ? 'bg-blue-100 text-blue-700' :
                      index === 2 ? 'bg-purple-100 text-purple-700' :
                      index === 3 ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                }`}
              >
                {tp.status === 'sent' ? <CheckCircle size={20} weight="fill" /> : tp.label}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-900">{tp.label}</span>
                    {getChannelIcon(tp.channel)}
                    <span className="text-xs text-slate-500 capitalize">{tp.tone}</span>
                  </div>
                  {getTouchpointStatusBadge(tp.status)}
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
                  <Calendar size={12} />
                  {tp.status === 'sent' ? `Enviado em ${formatDate(tp.sent_at)}` : `Agendado para ${formatDate(tp.scheduled_for)}`}
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed bg-white border border-slate-100 rounded p-2 mt-2">
                  {tp.message}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NurtureSequence;